<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import DataLayer from '@services/data-layer/index'
import Top from '@components/search/top'
import Mixes from '@components/search/mixes'
import Genres from '@components/search/genres'
import Artists from '@components/search/artists'
import Shows from '@components/search/shows'
import Tracklist from '@components/search/tracklist'
import uniqueFromArray from '@utils/unique-from-array'
import { useStore } from '@composables/use-store'

// `mainComponent` arrives as a route-supplied string. Vue 2's string-based
// component resolution does not see <script setup> imports, so map it here.
const MAIN_COMPONENTS = { Top, Mixes, Genres, Artists, Shows, Tracklist }

const TAB_LABELS = [
    ['search/results/top', 'Top Results'],
    ['search/results/mixes', 'Mixes'],
    ['search/results/genres', 'Genres'],
    ['search/results/artists', 'Artists'],
    ['search/results/shows', 'Shows'],
    ['search/results/tracklist', 'Tracklist'],
]

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Top',
    },
})

const store = useStore()

const searchResults = ref({})
const bestMatchType = ref('')
const bestMatchModel = ref({})
const searchComplete = ref(null)

const mainComponentDefinition = computed(() => MAIN_COMPONENTS[props.mainComponent])

const keyword = computed(() => store.state.search.keyword.replace(/ /g, '_'))

const hasLength = computed(() => {
    const results = searchResults.value || {}
    return !!Object.keys(results).reduce((total, key) => total + results[key].length, 0)
})

const tabs = computed(() =>
    TAB_LABELS.map(([name, label]) => ({
        to: { name, params: { keyword: keyword.value } },
        label,
    }))
)

function elasticSearch() {
    searchComplete.value = false
    DataLayer.elastic.search(keyword.value.replace(/_/g, ' ') || '').then(data => {
        searchResults.value = {
            Artists: (data.body && data.body.Artists) || [],
            Shows: (data.body && data.body.Shows) || [],
            Episodes: (data.body && data.body.Episodes) || [],
            Mixes: (data.body && data.body.Mixes) || [],
        }
        bestMatchType.value = data.headers['x-search-best-match'] || ''

        if (bestMatchType.value === 'Genres') {
            bestMatchModel.value = searchResults.value.Mixes
        } else if (bestMatchType.value && searchResults.value[bestMatchType.value]) {
            bestMatchModel.value = searchResults.value[bestMatchType.value][0]
        } else {
            bestMatchModel.value = undefined
        }

        searchComplete.value = true
    })
}

function searchJoin(limit, offset, resource = 'Mixes') {
    return DataLayer.elastic.search(keyword.value.replace(/_/g, ' ') || '', limit, offset).then(data => {
        const results = searchResults.value
        const resourceLength = results[resource].length

        results.Artists.push(...((data.body && uniqueFromArray(data.body.Artists, results.Artists)) || []))
        results.Shows.push(...((data.body && uniqueFromArray(data.body.Shows, results.Shows)) || []))
        results.Episodes.push(...((data.body && uniqueFromArray(data.body.Episodes, results.Episodes)) || []))
        results.Mixes.push(...((data.body && data.body.Mixes) || []))

        // A resource that did not grow reports one past the end, which stops the infinite scroll.
        return resourceLength === results[resource].length ? resourceLength + 1 : results.Mixes.length
    })
}

watch(keyword, () => {
    keyword.value && elasticSearch()
})

onMounted(() => {
    keyword.value && elasticSearch()
})
</script>

<template>
    <div
        v-if="keyword && keyword.length > 0 && hasLength && searchComplete"
        class="search-results"
    >
        <BaseTabPanel
            :tabs="tabs"
            class="search-results-tab-panel"
            is-underlined
            is-unpadded
            address-navigation
        />
        <component
            :is="mainComponentDefinition"
            class="search-results-tab-area"
            :search-results="searchResults"
            :search-join="searchJoin"
            :best-match-type="bestMatchType"
            :best-match-model="bestMatchModel"
        />
    </div>

    <BaseSection
        v-else-if="keyword && keyword.length && !hasLength && searchComplete"
        padded
    >
        <div class="search-results-not-found">
            <BaseIcon
                class="search-results-not-found-icon"
                name="flag"
            />
            <div class="search-results-not-found-head">No results found for "{{ keyword.replace(/_/g, ' ') }}"</div>
            <div class="search-results-not-found-sub">
                <span>Please make sure your words are spelled correctly or use less words</span>
            </div>
        </div>
    </BaseSection>
</template>

<style lang="scss" scoped>
@import '@design';

.search-results-not-found {
    @include center;
    @include sidePadding;

    width: 100%;
    text-align: center;

    @include desktop {
        width: auto;
        max-width: 570px;
    }

    &-icon {
        width: 64px;
        height: 64px;
        margin-bottom: $spacing-small;
        fill: $c-white;
    }

    &-head {
        @extend %h4;

        margin-bottom: $s-xs;
    }

    &-sub {
        @extend %contentTinyHeading;
    }
}

.search-results {
    @include mobileOnly {
        padding: 0;
    }
}

.search-results-tab-panel {
    padding-top: $s-s;
}

.search-results-tab-area {
    padding-top: $s-xxl;
}
</style>
